import { useCallback, useEffect, useState } from 'react'
import {
  getCampaign,
  listCampaignRecipients,
  getCampaignAnalytics,
  approveCampaign,
  rejectCampaign,
  pauseCampaign,
  resumeCampaign,
  deleteCampaign,
} from '../services/campaignsApi'
import type { Campaign, CampaignRecipient, CampaignRollup } from '../types'

// One combined hook rather than three separate ones — the Detail screen always
// needs all three together, and an approve/reject/pause/resume action always
// needs to refresh all three afterward (status changes, recipients get
// materialized, analytics shifts), so splitting them would just mean every
// action remembering to call three retry functions instead of one.
export function useCampaignDetail(workspaceId: string, campaignId: string) {
  const [campaign, setCampaign] = useState<Campaign | null>(null)
  const [recipients, setRecipients] = useState<CampaignRecipient[]>([])
  const [rollup, setRollup] = useState<CampaignRollup | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionPending, setActionPending] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  const load = useCallback(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    Promise.all([
      getCampaign(workspaceId, campaignId),
      listCampaignRecipients(workspaceId, campaignId),
      getCampaignAnalytics(workspaceId, campaignId),
    ])
      .then(([c, r, a]) => {
        if (cancelled) return
        setCampaign(c)
        setRecipients(r)
        setRollup(a)
      })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load this campaign') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [workspaceId, campaignId])

  useEffect(() => load(), [load])

  const runAction = useCallback(
    async (action: () => Promise<Campaign>) => {
      setActionPending(true)
      setActionError(null)
      try {
        await action()
        load()
      } catch (err) {
        setActionError(err instanceof Error ? err.message : 'Action failed')
      } finally {
        setActionPending(false)
      }
    },
    [load],
  )

  // Separate from runAction — on success there's nothing left to reload (the
  // campaign is gone), and the caller navigates away instead. Returns whether
  // the delete succeeded so the caller knows when to navigate.
  const remove = useCallback(async (): Promise<boolean> => {
    setActionPending(true)
    setActionError(null)
    try {
      await deleteCampaign(workspaceId, campaignId)
      return true
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to delete this campaign')
      return false
    } finally {
      setActionPending(false)
    }
  }, [workspaceId, campaignId])

  return {
    campaign,
    recipients,
    rollup,
    loading,
    error,
    retry: load,
    actionPending,
    actionError,
    approve: () => runAction(() => approveCampaign(workspaceId, campaignId)),
    reject: () => runAction(() => rejectCampaign(workspaceId, campaignId)),
    pause: () => runAction(() => pauseCampaign(workspaceId, campaignId)),
    resume: () => runAction(() => resumeCampaign(workspaceId, campaignId)),
    remove,
  }
}
